/**
 * Desktop app download utility
 *
 * Provides platform detection and download URL generation for JFrog Fly desktop app.
 * Supports: macOS ARM64, Windows x64, Windows ARM64
 */
import log from 'loglevel';

import { downloadFile } from './downloadFile';

// Platform constants
export const Platform = {
  DARWIN: 'darwin',
  WIN32: 'win32',
  LINUX: 'linux',
} as const;

export type Platform = (typeof Platform)[keyof typeof Platform];

// Architecture constants
export const Architecture = {
  X64: 'x64',
  ARM64: 'arm64',
} as const;

export type Architecture = (typeof Architecture)[keyof typeof Architecture];

// Platform config keys
export const PlatformKey = {
  DARWIN_ARM64: `${Platform.DARWIN}-${Architecture.ARM64}`,
  WIN32_X64: `${Platform.WIN32}-${Architecture.X64}`,
  WIN32_ARM64: `${Platform.WIN32}-${Architecture.ARM64}`,
} as const;

export type PlatformKey = (typeof PlatformKey)[keyof typeof PlatformKey];

export interface PlatformInfo {
  key: PlatformKey;
  platform: Platform;
  architecture: Architecture;
  displayName: string;
  filename: string;
}

const FLY_PUBLIC_BASE_URL = 'https://flyjfrog.jfrog.io/public/generic/fly-desktop';
const LATEST_VERSION_TOKEN = '[LATEST]';

const PLATFORM_CONFIG: Record<PlatformKey, PlatformInfo> = {
  [PlatformKey.DARWIN_ARM64]: {
    key: PlatformKey.DARWIN_ARM64,
    platform: Platform.DARWIN,
    architecture: Architecture.ARM64,
    displayName: 'macOS (Apple Silicon)',
    filename: 'JFrog-Fly.dmg',
  },
  [PlatformKey.WIN32_X64]: {
    key: PlatformKey.WIN32_X64,
    platform: Platform.WIN32,
    architecture: Architecture.X64,
    displayName: 'Windows (x64)',
    filename: 'JFrog%20Fly-Windows-x64-Setup.exe',
  },
  [PlatformKey.WIN32_ARM64]: {
    key: PlatformKey.WIN32_ARM64,
    platform: Platform.WIN32,
    architecture: Architecture.ARM64,
    displayName: 'Windows (ARM)',
    filename: 'JFrog%20Fly-Windows-arm64-Setup.exe',
  },
};

/**
 * Detect user's operating system from browser
 */
export const detectPlatform = (): Platform => {
  if (typeof navigator === 'undefined') {
    return Platform.DARWIN;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('mac')) {
    return Platform.DARWIN;
  }
  if (userAgent.includes('win')) {
    return Platform.WIN32;
  }
  if (userAgent.includes('linux')) {
    return Platform.LINUX;
  }

  return Platform.DARWIN;
};

/**
 * Detect CPU architecture based on platform defaults
 * Note: Browser APIs don't reliably expose architecture,
 * so we use sensible defaults (ARM64 for Mac, x64 for Windows)
 */
export const detectArchitecture = (): Architecture => {
  const platform = detectPlatform();

  if (platform === Platform.DARWIN) {
    return Architecture.ARM64;
  }

  return Architecture.X64;
};

/**
 * Get platform configuration by platform and architecture
 */
export const getPlatformConfig = (
  platform: Platform,
  architecture: Architecture
): PlatformInfo | null => {
  const key = `${platform}-${architecture}` as PlatformKey;
  return PLATFORM_CONFIG[key] || null;
};

/**
 * Get all available platform configurations
 */
export const getAllPlatforms = (): PlatformInfo[] => {
  return Object.values(PLATFORM_CONFIG);
};

/**
 * Generate download URL for specific platform configuration
 */
export const getDownloadUrl = (config: PlatformInfo): string => {
  return `${FLY_PUBLIC_BASE_URL}/${LATEST_VERSION_TOKEN}/${config.filename}`;
};

/**
 * Trigger download of the desktop app
 * If no config is provided, auto-detects platform and architecture
 */
export const downloadDesktopApp = (config?: PlatformInfo): void => {
  const platformConfig =
    config || getPlatformConfig(detectPlatform(), detectArchitecture());
  if (!platformConfig) {
    log.error('Unable to determine platform for download');
    return;
  }

  const downloadUrl = getDownloadUrl(platformConfig);
  downloadFile(downloadUrl, '_top');
};
